import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, CheckCircle, XCircle, Clock } from 'lucide-react';

const statusMap = {
    active: { icon: CheckCircle, color: 'text-green-500' },
    renewed: { icon: CheckCircle, color: 'text-green-500' },
    Expired: { icon: Clock, color: 'text-yellow-500' },
    expired: { icon: XCircle, color: 'text-red-500' },
    cancelled: { icon: XCircle, color: 'text-red-500' },
    inactive: { icon: Clock, color: 'text-gray-500' },
};
const PlanHistory = ({ userDetails, planHistory }) => {
    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: { y: 0, opacity: 1 }
    };
    const activePlans = planHistory.filter(p => p?.plan?.status === "active");
    const inactivePlans = planHistory.filter(p => p?.plan?.status === "inactive");
    const pastPlans = planHistory.filter(p => p?.plan?.status !== "active" && p?.plan?.status !== "inactive");

    return (
        <motion.div
            variants={itemVariants}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
        >
            <h3 className="text-xl font-bold text-gray-800 font-display mb-6">
                Plan History
            </h3>

            {(planHistory ?? []).length == 0 && <div>No History Found</div>}

            {/* ---------------- ACTIVE PLAN ---------------- */}
            {activePlans.length > 0 && (
                <div className="mb-6">
                    <h4 className="text-md font-semibold mb-3 text-gray-700">Active Plan</h4>

                    <div className="space-y-4">
                        {activePlans.map((plan, index) => {
                            const planDetails = plan?.plan;
                            const StatusIcon = statusMap[planDetails.status]?.icon || Clock;
                            const statusColor = statusMap[planDetails.status]?.color || "text-gray-500";

                            return (
                                <div key={index} className="flex items-start gap-4 p-4 bg-green-50 rounded-lg">
                                    <div className={`p-2 bg-white rounded-full border ${statusColor}`}>
                                        <StatusIcon size={20} />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex justify-between items-center">
                                            <p className="font-semibold text-gray-800">{planDetails.name}</p>
                                            <p className="font-semibold text-gray-800">{planDetails.price} OMR</p>
                                        </div>

                                        <div className="flex justify-between items-center">
                                            <div className="flex gap-5 items-center  text-sm">
                                                <p > <span className="text-gray-500"> Total {planDetails.access_type}</span> : {planDetails.duration}</p>
                                                {planDetails.access_type == 'session' && <p><span className="text-gray-500"> Remaining</span> : {planDetails.remaining_sessions}</p>}
                                            </div>
                                            <p className={`text-sm font-medium ${statusColor} capitalize`}>{planDetails.status}</p>
                                        </div>
                                        <div className="text-sm text-gray-500 mt-1 flex items-center gap-2">
                                            {!!planDetails.activation_date && <div className="text-sm text-gray-500 mt-1 flex items-center gap-2">
                                                <Calendar size={14} />
                                                <span>Activation Date : {planDetails?.activation_date
                                                    ? new Date(planDetails.activation_date).toISOString().split("T")[0]
                                                    : ""}
                                                </span>
                                            </div>}
                                            {(!!planDetails.renewal_date && planDetails?.access_type == 'session') && <div className="text-sm text-gray-500 mt-1 flex items-center gap-2">
                                                <Calendar size={14} />
                                                <span>Renewal Date : {planDetails?.renewal_date
                                                    ? new Date(planDetails.renewal_date).toISOString().split("T")[0]
                                                    : ""}
                                                </span>
                                            </div>}
                                        </div>

                                        {!!planDetails.expiry_date && (
                                            <div className="text-sm text-gray-500 mt-1 flex items-center gap-2">
                                                <Calendar size={14} />
                                                <span>Expiry Date: {planDetails?.expiry_date
                                                    ? new Date(planDetails.expiry_date).toISOString().split("T")[0]
                                                    : ""}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ---------------- PAST PLANS ---------------- */}
            {inactivePlans.length > 0 && (
                <div className='mb-5'>
                    <h4 className="text-md font-semibold mb-3 text-gray-700">Future Plans</h4>

                    <div className="space-y-4">
                        {inactivePlans.map((plan, index) => {
                            const planDetails = plan.plan;
                            const StatusIcon = statusMap[planDetails.status]?.icon || Clock;
                            const statusColor = statusMap[planDetails.status]?.color || "text-gray-500";

                            return (
                                <div key={index} className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg">
                                    <div className={`p-2 bg-white rounded-full border ${statusColor}`}>
                                        <StatusIcon size={20} />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex justify-between items-center">
                                            <p className="font-semibold text-gray-800">{planDetails.name}</p>
                                            <p className="font-semibold text-gray-800">{planDetails.price} OMR</p>
                                        </div>

                                        <div className="flex justify-between items-center">
                                            <p className="text-gray-500 text-sm">
                                                Total {planDetails.access_type} : {planDetails.duration}
                                            </p>
                                            <p className={`text-sm font-medium ${statusColor} capitalize`}>{planDetails.status}</p>
                                        </div>

                                        {!!planDetails.expiry_date && (
                                            <div className="text-sm text-gray-500 mt-1 flex items-center gap-2">
                                                <Calendar size={14} />
                                                <span>Expiry Date: {new Date(planDetails.expiry_date).toLocaleDateString()}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            {/* ---------------- PAST PLANS ---------------- */}
            {pastPlans.length > 0 && (
                <div>
                    <h4 className="text-md font-semibold mb-3 text-gray-700">Past Plans</h4>

                    <div className="space-y-4">
                        {pastPlans.map((plan, index) => {
                            const planDetails = plan.plan;
                            const StatusIcon = statusMap[planDetails.status]?.icon || Clock;
                            const statusColor = statusMap[planDetails.status]?.color || "text-gray-500";

                            return (
                                <div key={index} className="flex items-start gap-4 p-4 bg-gray-100 rounded-lg">
                                    <div className={`p-2 bg-white rounded-full border ${statusColor}`}>
                                        <StatusIcon size={20} />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex justify-between items-center">
                                            <p className="font-semibold text-gray-800">{planDetails.name}</p>
                                            <p className="font-semibold text-gray-800">{planDetails.price} OMR</p>
                                        </div>

                                        <div className="flex justify-between items-center">
                                            <p className="text-gray-500 text-sm">
                                                Total {planDetails.access_type} : {planDetails.duration}
                                            </p>
                                            <p className={`text-sm font-medium ${statusColor} capitalize`}>{planDetails.status}</p>
                                        </div>

                                        {!!planDetails.expiry_date && (
                                            <div className="text-sm text-gray-500 mt-1 flex items-center gap-2">
                                                <Calendar size={14} />
                                                <span>Expiry Date: {new Date(planDetails.expiry_date).toLocaleDateString()}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </motion.div>
    );
};

export default PlanHistory;
